"use client";

import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils"; // Adjust the import path if needed
import { Button } from "@/components/ui/button";
import { Calendar, type CalendarProps } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { addYears, isAfter, isBefore, startOfToday } from "date-fns";

interface DateRangePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  date: DateRange | undefined;
  onDateChange: (date: DateRange | undefined) => void;
  disabled?: CalendarProps["disabled"];
}

export function DateRangePicker({
  className,
  date,
  onDateChange,
  disabled,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);
  const today = startOfToday();
  const maxDate = addYears(today, 1);

  const disabledMatcher = React.useMemo<CalendarProps["disabled"]>(() => {
    const base = (date: Date) =>
      isBefore(date, today) || isAfter(date, maxDate);

    if (!disabled) {
      return base;
    }

    return Array.isArray(disabled) ? [base, ...disabled] : [base, disabled];
  }, [disabled, maxDate, today]);

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-[300px] justify-start text-left font-normal",
              !date && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            autoFocus
            mode="range"
            defaultMonth={date?.from}
            selected={date}
            onSelect={(newDate) => {
              onDateChange(newDate);
              if (newDate?.from && newDate?.to) {
                setOpen(false);
              }
            }}
            numberOfMonths={2}
            disabled={disabledMatcher}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
